import React, { useState, forwardRef, useEffect } from 'react';
import { Input } from './input';
import { Label } from './label';
import { cn } from '../../lib/utils';
import { Eye, EyeOff, AlertCircle, CheckCircle, Info } from 'lucide-react';

/**
 * Enhanced Form Input with validation, password toggle, shake animation, and inline hints
 * 
 * Features:
 * - Password visibility toggle (eye icon)
 * - Inline error messages with animation
 * - Shake animation on errors
 * - Proactive validation hints
 * - Real-time validation on blur/change
 * - Success state indication
 */
export const FormInput = forwardRef(({
  id,
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  success,
  hint,
  required,
  minLength,
  maxLength,
  pattern,
  patternMessage,
  showPasswordToggle = false,
  shake = false,
  className,
  inputClassName,
  disabled,
  rightElement,
  validateOnChange = false,
  showRequiredHint = true,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [isShaking, setIsShaking] = useState(false);

  // Trigger shake animation when shake prop changes
  useEffect(() => {
    if (shake) {
      setIsShaking(true);
      const timer = setTimeout(() => setIsShaking(false), 500);
      return () => clearTimeout(timer);
    }
  }, [shake]);

  const inputType = showPasswordToggle && type === 'password' 
    ? (showPassword ? 'text' : 'password') 
    : type;

  const validateInput = (inputValue) => {
    if (required && !inputValue) {
      return `${label || 'This field'} is required`;
    }
    if (minLength && inputValue && inputValue.length < minLength) {
      return `Minimum ${minLength} characters required`;
    }
    if (maxLength && inputValue && inputValue.length > maxLength) {
      return `Maximum ${maxLength} characters allowed`;
    }
    if (pattern && inputValue && !new RegExp(pattern).test(inputValue)) {
      return patternMessage || 'Invalid format';
    }
    if (type === 'email' && inputValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const handleBlur = (e) => {
    setTouched(true);
    const validationError = validateInput(e.target.value);
    setLocalError(validationError);
    
    if (validationError) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
    
    onBlur?.(e);
  };

  const handleChange = (e) => {
    // Clear local error when user starts typing
    if (localError || validateOnChange) {
      const validationError = validateOnChange ? validateInput(e.target.value) : null;
      setLocalError(validationError);
    }
    onChange?.(e);
  };

  const displayError = error || (touched && localError);
  const hasError = !!displayError;
  const hasSuccess = success && !hasError && touched && value;

  // Generate validation hints
  const getValidationHints = () => {
    const hints = [];
    if (required && showRequiredHint) hints.push('Required');
    if (minLength) hints.push(`Min ${minLength} chars`);
    if (maxLength) hints.push(`Max ${maxLength} chars`);
    if (type === 'email') hints.push('Valid email format');
    return hints;
  };

  const validationHints = getValidationHints();
  const displayHint = hint || (validationHints.length > 0 ? validationHints.join(' • ') : null);

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <Label htmlFor={id} className="flex items-center gap-1 text-sm font-medium">
          {label}
          {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      
      <div className="relative">
        <Input
          ref={ref}
          id={id}
          type={inputType}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          className={cn(
            inputClassName,
            hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50/50',
            hasSuccess && 'border-green-500 focus:border-green-500 focus:ring-green-500',
            (showPasswordToggle || rightElement) && 'pr-10',
            isShaking && 'animate-shake'
          )}
          {...props}
        />
        
        {/* Password visibility toggle */}
        {showPasswordToggle && type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        )}
        
        {/* Custom right element */}
        {rightElement && !showPasswordToggle && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightElement}
          </div>
        )}
        
        {/* Success icon */}
        {hasSuccess && !showPasswordToggle && !rightElement && (
          <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
        )}
      </div>
      
      {/* Error message with animation */}
      {hasError && (
        <p className="text-sm text-red-600 flex items-center gap-1.5 animate-fadeIn">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{displayError}</span>
        </p>
      )}
      
      {/* Success message */}
      {hasSuccess && success !== true && typeof success === 'string' && (
        <p className="text-sm text-green-600 flex items-center gap-1.5 animate-fadeIn">
          <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{success}</span>
        </p>
      )}
      
      {/* Hint text (show when no error) */}
      {displayHint && !hasError && (
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Info className="h-3 w-3 flex-shrink-0" />
          {displayHint}
        </p>
      )}
    </div>
  );
});

FormInput.displayName = 'FormInput';

/**
 * Password Input with built-in visibility toggle and strength indicator
 */
export const PasswordInput = forwardRef(({
  showStrength = false,
  label = 'Password',
  hint,
  ...props
}, ref) => {
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { level: 1, text: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { level: 2, text: 'Medium', color: 'bg-yellow-500' };
    return { level: 3, text: 'Strong', color: 'bg-green-500' };
  };

  const strength = showStrength ? getPasswordStrength(props.value) : null;
  
  // Generate password hints
  const passwordHint = hint || 'Min 8 characters with uppercase, lowercase & number';

  return (
    <div className="space-y-2">
      <FormInput
        ref={ref}
        type="password"
        label={label}
        showPasswordToggle={true}
        hint={!showStrength ? passwordHint : undefined}
        minLength={8}
        {...props}
      />
      
      {showStrength && props.value && (
        <div className="space-y-1.5">
          <div className="flex gap-1">
            {[1, 2, 3].map((level) => (
              <div
                key={level}
                className={cn(
                  "h-1.5 flex-1 rounded-full transition-all duration-300",
                  level <= strength.level ? strength.color : 'bg-gray-200'
                )}
              />
            ))}
          </div>
          <p className={cn(
            "text-xs font-medium",
            strength.level === 1 && "text-red-600",
            strength.level === 2 && "text-yellow-600",
            strength.level === 3 && "text-green-600"
          )}>
            Password strength: {strength.text}
          </p>
        </div>
      )}
    </div>
  );
});

PasswordInput.displayName = 'PasswordInput';

/**
 * Email Input with built-in email validation
 */
export const EmailInput = forwardRef(({
  label = 'Email',
  placeholder = 'email@example.com',
  ...props
}, ref) => {
  return (
    <FormInput
      ref={ref}
      type="email"
      label={label}
      placeholder={placeholder}
      pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
      patternMessage="Please enter a valid email address"
      {...props}
    />
  );
});

EmailInput.displayName = 'EmailInput';

export default FormInput;
